import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "../api/types";
import { useAuth } from "../store/auth";
import { useLocale } from "../hooks/useLocale";
import styles from "./LoginPage.module.css";

type State = "loading" | "ok" | "error";

export function ConfirmEmailPage() {
  const { t, tOr } = useLocale();
  const confirmEmail = useAuth((s) => s.confirmEmail);
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState<State>("loading");
  const [errCode, setErrCode] = useState<string | null>(null);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get("token");
    if (!token) {
      setState("error");
      setErrCode("token_invalid");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const target = await confirmEmail(token);
        if (cancelled) return;
        setState("ok");
        // Brief pause so the user reads the success message, then navigate.
        setTimeout(() => navigate(target, { replace: true }), 1000);
      } catch (err) {
        if (cancelled) return;
        setState("error");
        setErrCode(err instanceof ApiError ? err.code : "unknown");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, confirmEmail, navigate]);

  return (
    <main className={styles.shell}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t("confirm.title")}</h1>
        {state === "loading" && (
          <p className={styles.subtitle}>{t("confirm.loading")}</p>
        )}
        {state === "ok" && (
          <p className={styles.success}>{t("confirm.ok")}</p>
        )}
        {state === "error" && (
          <>
            <p className={styles.error}>
              {tOr(`confirm.error.${errCode}`, "confirm.error.unknown")}
            </p>
            <Link to="/login" className={styles.submit}>
              {t("confirm.back_to_login")}
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
